import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import { UserPlus, Shield, Edit, Trash2, Power, PowerOff } from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_COLORS = {
  admin: { bg: '#FEE2E2', color: '#991B1B' },
  staff: { bg: '#DBEAFE', color: '#1E40AF' },
  doctor: { bg: '#D1FAE5', color: '#065F46' },
};

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', role: 'staff' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/users`);
      setUsers(data);
    } catch (e) {
      console.error('Error fetching users:', e);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (editingUser) {
        // Update: send password only if user typed a new one
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await axios.put(`${API}/auth/users/${editingUser.id}`, { ...formData, password: formData.password || '' });
      } else {
        await axios.post(`${API}/auth/users`, formData);
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({ email: u.email, password: '', name: u.name, role: u.role });
    setShowForm(true);
    setError('');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/auth/users/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleActive = async (u) => {
    const isActive = u.active !== false;
    const action = isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${u.name} (${u.email})?`)) return;
    try {
      await axios.post(`${API}/auth/users/${u.id}/${action}`);
      fetchUsers();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || `Failed to ${action} user`);
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', name: '', role: 'staff' });
    setEditingUser(null);
    setShowForm(false);
    setError('');
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="users-title">
            Users
          </h1>
          <p className="mt-2" style={{ color: '#6B7280' }}>Manage staff, doctors, and admin accounts</p>
        </div>
        <button className="primary-btn flex items-center gap-2" onClick={() => setShowForm(true)} data-testid="add-user-btn">
          <UserPlus size={20} />
          Add User
        </button>
      </div>

      {showForm && (
        <div className="card p-6 mb-8" data-testid="user-form">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            {editingUser ? `Edit User: ${editingUser.name}` : 'Add New User'}
          </h2>
          <p className="text-sm mb-4 p-3 rounded-lg" style={{ backgroundColor: '#F5E6F5', color: '#990099' }}>
            {editingUser
              ? 'Leave the password field empty to keep the existing password.'
              : 'User will be created with the specified role. They can login using their email and password.'}
          </p>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label>Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="user-name-input" />
              </div>
              <div>
                <label>Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="user-email-input" />
              </div>
              <div>
                <label>{editingUser ? 'New Password (optional)' : 'Password'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  minLength={editingUser ? 0 : 6}
                  placeholder={editingUser ? 'Leave empty to keep current' : 'Min 6 characters'}
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <label>Role</label>
                <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} data-testid="user-role-select">
                  <option value="admin">Admin</option>
                  <option value="staff">Staff</option>
                  <option value="doctor">Doctor</option>
                </select>
              </div>
            </div>
            {error && (
              <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }} data-testid="user-form-error">{error}</div>
            )}
            <div className="flex gap-3 mt-6">
              <button type="submit" className="primary-btn" disabled={loading} data-testid="user-submit-btn">
                {loading ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
              </button>
              <button type="button" className="secondary-btn" onClick={resetForm} data-testid="user-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-6">
        <div className="overflow-x-auto">
          <table data-testid="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const badge = ROLE_COLORS[u.role] || ROLE_COLORS.staff;
                const isSelf = currentUser?.id === u.id;
                const isActive = u.active !== false;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} style={!isActive ? { backgroundColor: '#F3F4F6', opacity: 0.85 } : {}}>
                    <td className="font-medium" style={{ color: '#2D3A2A' }}>
                      <div className="flex items-center gap-2">
                        <Shield size={16} style={{ color: '#990099' }} />
                        {u.name}
                        {isSelf && <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F5E6F5', color: '#990099' }}>You</span>}
                        {!isActive && (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }} data-testid={`inactive-badge-${u.id}`}>
                            INACTIVE
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium uppercase tracking-wide" style={{ backgroundColor: badge.bg, color: badge.color }}>
                        {u.role}
                      </span>
                    </td>
                    <td style={{ color: '#6B7280' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEdit(u)}
                          className="p-2 rounded hover:bg-gray-100 transition-colors"
                          title="Edit"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Edit size={18} style={{ color: '#990099' }} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(u)}
                          disabled={isSelf}
                          className="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={isSelf ? "Cannot deactivate yourself" : (isActive ? "Deactivate" : "Activate")}
                          data-testid={`toggle-active-user-${u.id}`}
                        >
                          {isActive ? (
                            <PowerOff size={18} style={{ color: '#92400E' }} />
                          ) : (
                            <Power size={18} style={{ color: '#065F46' }} />
                          )}
                        </button>
                        <button
                          onClick={() => setDeleteTarget(u)}
                          disabled={isSelf}
                          className="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={isSelf ? "Cannot delete yourself" : "Delete"}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 size={18} style={{ color: '#EF4444' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete User"
        message={`Are you sure you want to delete ${deleteTarget?.name} (${deleteTarget?.email})? This will permanently revoke their access. This action cannot be undone.`}
        confirmLabel="Delete User"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        testIdPrefix="delete-user"
      />
    </div>
  );
};

export default Users;
